import { Box, FormControlLabel, FormGroup, Switch } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { formatHoursMinutes } from "@repo/legacy/components/helper/Index";
import List from "@repo/legacy/components/shared/lists/List";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useMemo } from "react";

import { useAllProjectTimeSummaryDataSource } from "../../hooks/AllProjectTimeSummaryDataSource";

import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import type { ProjectTimeSummary } from "@repo/shared-project/types/ProjectTypes";

const StatusAsProgress = ({ row }: { row: ProjectTimeSummary }) => {
	const { t } = useTranslation();

	const workedPercent = useMemo(() => {
		return ((row.workedMinutes / row.totalMinutes) * 100).toFixed(2);
	}, [row]);

	return (
		<Box
			className="progressBarWrapper"
			sx={{
				border: "none",
				width: "100%",
				padding: 0,
				margin: 0,
				display: "block",
				position: "relative",
			}}>
			<Box
				className="progressBarSection"
				sx={{ width: "100%", margin: 0, padding: 0, position: "relative" }}>
				<span
					className="font10"
					style={{
						position: "absolute",
						top: "-10px",
					}}>{`${t("status")} ${workedPercent}% `}</span>
				<div
					className="progressBar left"
					style={{ width: "100%", margin: 0, padding: 0, position: "absolute", top: "25px" }}>
					<div
						style={{
							width: `${workedPercent}%`,
							backgroundColor: "lightgreen",
							borderTopLeftRadius: "10px",
							borderBottomLeftRadius: "10px",
							left: 0,
						}}
						className="progressBarItem"
					/>
				</div>
			</Box>
		</Box>
	);
};

export const ProjectAllTimeSummary = () => {
	const { t, i18n } = useTranslation();

	const allProjectSummaryDataSource = useAllProjectTimeSummaryDataSource();

	const [openYear, setOpenYear] = React.useState<boolean>(false);

	const columns: GridColDef<ProjectTimeSummary>[] = useMemo(
		() => [
			{
				field: "projectName",
				headerName: t("project-name"),
				width: 350,
				renderCell(params: GridCellParams<ProjectTimeSummary>) {
					return `${params.row.projectName}`;
				},
			},
			{
				field: "positionName",
				headerName: t("position"),
				width: 350,
				renderCell(params: GridCellParams<ProjectTimeSummary>) {
					return `${params.row.positionName}`;
				},
			},
			{
				field: "workedHours",
				headerName: t("worked-hours"),
				width: 150,
				renderCell(params: GridCellParams<ProjectTimeSummary>) {
					return formatHoursMinutes(params.row.workedMinutes);
				},
			},
			{
				field: "totalHours",
				headerName: t("total-hours"),
				width: 150,
				renderCell(params: GridCellParams<ProjectTimeSummary>) {
					return formatHoursMinutes(params.row.totalMinutes);
				},
			},
			{
				field: "status",
				headerName: t("status"),
				flex: 1,
				width: 350,
				renderCell(params: GridCellParams<ProjectTimeSummary>) {
					return <StatusAsProgress row={params.row} />;
				},
			},
		],

		[t, i18n.language, allProjectSummaryDataSource],
	);

	return (
		<List
			columns={columns as GridCellParams<ProjectTimeSummary>[]}
			dataSource={allProjectSummaryDataSource}
			hideFooter={true}
			showSearch={false}>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
				<DatePicker
					className="left w-30"
					open={openYear}
					onClose={() => {
						setOpenYear(false);
					}}
					onOpen={() => {
						setOpenYear(true);
					}}
					label={t("year")}
					views={["year"]}
					value={dayjs(allProjectSummaryDataSource.filter.year)}
					onChange={(newValue) => {
						allProjectSummaryDataSource.setFilter?.({
							...allProjectSummaryDataSource.filter,
							year: (newValue as dayjs.Dayjs).format("YYYY-MM-DD"),
						});
					}}
					slotProps={{
						textField: {
							onClick: () => {
								setOpenYear(!openYear);
							},
						},
					}}
				/>
			</LocalizationProvider>
			<FormGroup className="padding7 left" sx={{ ml: 2.5 }}>
				<FormControlLabel
					control={
						<Switch
							onChange={(event) => {
								if (allProjectSummaryDataSource.setFilter) {
									allProjectSummaryDataSource.setFilter({
										...allProjectSummaryDataSource.filter,
										showDeleted: event.target.checked,
									});
								}
							}}
							checked={allProjectSummaryDataSource.filter.showDeleted}
						/>
					}
					label={t("show-inactive")}
				/>
			</FormGroup>
		</List>
	);
};
